import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CreditCard, Package, CheckCircle, Loader2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const Checkout = () => {
  const { cartItems, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [orderComplete, setOrderComplete] = useState(false);
  const [shipping, setShipping] = useState(0);
  const [isCalculatingShipping, setIsCalculatingShipping] = useState(false);
  const [discountCode, setDiscountCode] = useState('');
  const [appliedCodes, setAppliedCodes] = useState([]);
  const [isValidatingCode, setIsValidatingCode] = useState(false);
  const [freeShipping, setFreeShipping] = useState(false);

  const discountAmount = appliedCodes.reduce((sum, c) => sum + (c.discountAmount || 0), 0);
  const appliedCode = appliedCodes.map(c => c.code).join(' + ');
  
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleStateChange = async (value) => {
    setFormData({
      ...formData,
      state: value
    });
    
    // Calculate shipping when state is selected
    if (value && subtotal > 0) {
      await calculateShipping(value, subtotal);
    }
  };

  const totalWeight = cartItems.reduce((sum, item) => sum + (item.weight || 0) * item.quantity, 0);

  const calculateShipping = async (state, orderSubtotal) => {
    if (!state) return;
    
    setIsCalculatingShipping(true);
    try {
      const response = await fetch('/api/payments/calculate-shipping', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          state: state,
          subtotal: orderSubtotal,
          totalWeight: totalWeight
        }),
      });

      if (!response.ok) throw new Error('Failed to calculate shipping');

      const data = await response.json();
      setShipping(data.shipping_cost);
    } catch (error) {
      console.error('Error calculating shipping:', error);
      toast({
        title: "Error",
        description: "Failed to calculate shipping. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsCalculatingShipping(false);
    }
  };

  const applyDiscountCode = async () => {
    if (!discountCode.trim()) return;

    const codeUpper = discountCode.trim().toUpperCase();
    if (appliedCodes.find(c => c.code === codeUpper)) {
      toast({ title: "Already Applied", description: "This code is already applied.", variant: "destructive" });
      return;
    }

    if (appliedCodes.length > 0) {
      const existingAllStackable = appliedCodes.every(c => c.stackable);
      if (!existingAllStackable) {
        toast({ title: "Can't Stack Codes", description: "Only one promo code can be used per order. Remove the current code first.", variant: "destructive" });
        return;
      }
    }
    
    setIsValidatingCode(true);
    try {
      const response = await fetch('/api/discounts/validate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: discountCode, subtotal: subtotal, customerEmail: formData.email || null }),
      });

      const data = await response.json();
      
      if (data.valid) {
        if (appliedCodes.length > 0 && !data.stackable && !appliedCodes.every(c => c.stackable)) {
          toast({ title: "Can't Stack Codes", description: "This code can't be combined with other codes.", variant: "destructive" });
          return;
        }

        const newCode = { code: data.code, discountAmount: data.discountAmount, discountType: data.discountType, stackable: data.stackable };
        setAppliedCodes(prev => [...prev, newCode]);
        if (data.discountType === 'free_shipping') {
          setFreeShipping(true);
        }
        setDiscountCode('');
        toast({ title: "Discount Applied!", description: data.message, duration: 3000 });
      } else {
        toast({ title: "Invalid Code", description: data.message, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to validate discount code.", variant: "destructive" });
    } finally {
      setIsValidatingCode(false);
    }
  };

  const removeDiscount = (codeToRemove) => {
    const updated = codeToRemove
      ? appliedCodes.filter(c => c.code !== codeToRemove)
      : [];
    setAppliedCodes(updated);
    if (!updated.find(c => c.discountType === 'free_shipping')) {
      setFreeShipping(false);
    }
    if (!codeToRemove) setDiscountCode('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.state) {
      toast({
        title: "Missing State",
        description: "Please select your state to calculate shipping.",
        variant: "destructive",
      });
      return;
    }
    
    setIsProcessing(true);

    try {
      const orderData = {
        customerInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode
        },
        items: cartItems.map(item => ({
          productId: item.id,
          name: item.name,
          size: item.size,
          price: item.price,
          quantity: item.quantity,
          variant: item.selectedVariant || null
        })),
        subtotal: subtotal,
        shipping: effectiveShipping,
        tax: tax,
        total: total,
        origin_url: window.location.origin
      };

      const response = await fetch('/api/payments/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(orderData),
      });

      if (!response.ok) throw new Error('Failed to create checkout session');

      const data = await response.json();
      
      // Redirect to Stripe checkout
      window.location.href = data.url;
      
    } catch (error) {
      console.error('Error creating checkout:', error);
      setIsProcessing(false);
      toast({
        title: "Error",
        description: "Failed to process checkout. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
    }
  };

  if (cartItems.length === 0 && !orderComplete) {
    return (
      <div className="min-h-screen py-20" style={{ backgroundColor: '#f7f5f2' }}>
        <div className="container mx-auto px-4 text-center">
          <Package className="h-24 w-24 mx-auto mb-6" style={{ color: '#736c64' }} />
          <h2 className="text-3xl font-bold mb-4" style={{ color: '#4a2c1a' }}>
            Your cart is empty
          </h2>
          <p className="text-lg mb-8" style={{ color: '#736c64' }}>
            Add some delicious maple syrup to your cart to continue
          </p>
          <Button
            onClick={() => navigate('/shop')}
            className="text-white font-semibold px-8 py-6 text-lg"
            style={{ backgroundColor: '#FF8C19' }}
          >
            Continue Shopping
          </Button>
        </div>
      </div>
    );
  }

  if (orderComplete) {
    return (
      <div className="min-h-screen py-20" style={{ backgroundColor: '#f7f5f2' }}>
        <div className="container mx-auto px-4 max-w-2xl text-center">
          <div className="bg-white rounded-lg shadow-lg p-12">
            <CheckCircle className="h-24 w-24 mx-auto mb-6" style={{ color: '#22c55e' }} />
            <h1 className="text-4xl font-bold mb-4" style={{ color: '#4a2c1a' }}>
              Order Complete!
            </h1>
            <p className="text-xl mb-8" style={{ color: '#736c64' }}>
              Thank you for your order! We'll send you a confirmation email shortly.
            </p>
            <div className="space-y-4">
              <Button
                onClick={() => navigate('/shop')}
                className="w-full text-white font-semibold py-6 text-lg"
                style={{ backgroundColor: '#FF8C19' }}
              >
                Continue Shopping
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                className="w-full font-semibold py-6 text-lg border-2"
                style={{ borderColor: '#FF8C19', color: '#FF8C19' }}
              >
                Return to Home
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const subtotal = getCartTotal();
  const tax = 0;
  const effectiveShipping = freeShipping ? 0 : shipping;
  const total = subtotal - discountAmount + effectiveShipping;

  return (
    <div className="min-h-screen py-12" style={{ backgroundColor: '#f7f5f2' }}>
      <div className="container mx-auto px-4 max-w-6xl">
        <h1 className="text-4xl md:text-5xl font-bold mb-8" style={{ color: '#4a2c1a', fontFamily: 'Georgia, serif' }}>
          Checkout
        </h1>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Checkout Form */}
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Shipping Information */}
              <div className="bg-white rounded-lg shadow-lg p-8">
                <h2 className="text-2xl font-bold mb-6" style={{ color: '#4a2c1a' }}>
                  Shipping Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input id="firstName" name="firstName" required value={formData.firstName} onChange={handleChange} />
                  </div>
                  <div>
                    <Label htmlFor="lastName">Last Name *</Label>
                    <Input id="lastName" name="lastName" required value={formData.lastName} onChange={handleChange} />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" name="email" type="email" required value={formData.email} onChange={handleChange} />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input id="phone" name="phone" type="tel" required value={formData.phone} onChange={handleChange} />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="address">Address *</Label>
                    <Input id="address" name="address" required value={formData.address} onChange={handleChange} />
                  </div>
                  <div>
                    <Label htmlFor="city">City *</Label>
                    <Input id="city" name="city" required value={formData.city} onChange={handleChange} />
                  </div>
                  <div>
                    <Label htmlFor="state">State *</Label>
                    <Select value={formData.state} onValueChange={handleStateChange} required>
                      <SelectTrigger>
                        <SelectValue placeholder="Select state" />
                      </SelectTrigger>
                      <SelectContent>
                        {US_STATES.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="zipCode">Zip Code *</Label>
                    <Input id="zipCode" name="zipCode" required value={formData.zipCode} onChange={handleChange} />
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              <div className="bg-white rounded-lg shadow-lg p-8">
                <div className="flex items-center mb-6">
                  <CreditCard className="h-6 w-6 mr-3" style={{ color: '#FF8C19' }} />
                  <h2 className="text-2xl font-bold" style={{ color: '#4a2c1a' }}>
                    Payment
                  </h2>
                </div>
                <div className="bg-blue-50 border-2 border-blue-400 rounded-lg p-4 mb-6">
                  <p className="text-sm font-semibold" style={{ color: '#1e40af' }}>
                    🔒 Secure Payment with Stripe - You'll be redirected to complete your payment securely.
                  </p>
                </div>
                {shipping > 0 && formData.state && (
                  <div className="p-4 rounded-lg" style={{ backgroundColor: '#f7f5f2' }}>
                    <p className="text-sm">
                      <strong style={{ color: '#4a2c1a' }}>Shipping to {formData.state}:</strong>{' '}
                      {freeShipping ? (
                        <span>
                          <span className="line-through mr-2" style={{ color: '#736c64' }}>${shipping.toFixed(2)}</span>
                          <span style={{ color: '#22c55e' }}>FREE</span>
                        </span>
                      ) : (
                        <span style={{ color: '#FF8C19' }}>${shipping.toFixed(2)}</span>
                      )}
                    </p>
                    {freeShipping && (
                      <p className="text-sm mt-2" style={{ color: '#22c55e' }}>
                        Local pickup - free shipping with code {appliedCode}!
                      </p>
                    )}
                  </div>
                )}
                {isCalculatingShipping && (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-6 w-6 animate-spin mr-2" style={{ color: '#FF8C19' }} />
                    <span style={{ color: '#736c64' }}>Calculating shipping...</span>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                disabled={isProcessing || !formData.state || isCalculatingShipping}
                className="w-full text-white font-semibold py-6 text-lg hover:opacity-90 transition-opacity"
                style={{ backgroundColor: '#FF8C19' }}
              >
                {isProcessing ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  `Proceed to Payment - $${total.toFixed(2)}`
                )}
              </Button>
            </form>
          </div>

          {/* Order Summary */}
          <div>
            <div className="bg-white rounded-lg shadow-lg p-8 sticky top-24">
              <h2 className="text-2xl font-bold mb-6" style={{ color: '#4a2c1a' }}>
                Order Summary
              </h2>
              <div className="space-y-4 mb-6">
                {cartItems.map((item) => (
                  <div key={item.id} className="flex justify-between">
                    <div>
                      <p className="font-semibold" style={{ color: '#4a2c1a' }}>{item.name}</p>
                      <p className="text-sm" style={{ color: '#736c64' }}>Qty: {item.quantity}</p>
                    </div>
                    <p className="font-semibold" style={{ color: '#4a2c1a' }}>
                      ${(item.price * item.quantity).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
              
              {/* Discount Code Section */}
              <div className="border-t mt-4 pt-4 mb-4" style={{ borderColor: '#e5d5c0' }}>
                <h3 className="text-sm font-semibold mb-3" style={{ color: '#4a2c1a' }}>
                  Have a discount code?
                </h3>
                {appliedCodes.length === 0 ? (
                  <div className="flex space-x-2">
                    <Input
                      placeholder="Enter code"
                      value={discountCode}
                      onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                      className="flex-1"
                    />
                    <Button
                      onClick={applyDiscountCode}
                      disabled={isValidatingCode || !discountCode}
                      variant="outline"
                      className="border-2"
                      style={{ borderColor: '#FF8C19', color: '#FF8C19' }}
                    >
                      {isValidatingCode ? 'Checking...' : 'Apply'}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {appliedCodes.map((c) => (
                      <div key={c.code} className="bg-green-50 border-2 border-green-400 rounded-lg p-3 flex items-center justify-between">
                        <div>
                          <p className="text-sm font-semibold" style={{ color: '#22c55e' }}>
                            Code: {c.code}
                            {c.stackable && <span className="text-xs ml-2 px-1.5 py-0.5 rounded-full bg-blue-100" style={{ color: '#1e40af' }}>Stackable</span>}
                          </p>
                          <p className="text-xs" style={{ color: '#166534' }}>
                            {c.discountType === 'free_shipping' ? 'Free shipping applied' : `-$${c.discountAmount.toFixed(2)} discount applied`}
                          </p>
                        </div>
                        <Button onClick={() => removeDiscount(c.code)} variant="ghost" size="sm" style={{ color: '#dc2626' }}>
                          Remove
                        </Button>
                      </div>
                    ))}
                    {appliedCodes.some(c => c.stackable) && (
                      <div className="flex space-x-2 mt-2">
                        <Input
                          placeholder="Add another code"
                          value={discountCode}
                          onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                          className="flex-1"
                        />
                        <Button
                          onClick={applyDiscountCode}
                          disabled={isValidatingCode || !discountCode}
                          variant="outline"
                          className="border-2"
                          style={{ borderColor: '#FF8C19', color: '#FF8C19' }}
                        >
                          {isValidatingCode ? 'Checking...' : 'Apply'}
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <div className="border-t pt-4 space-y-2" style={{ borderColor: '#e5d5c0' }}>
                <div className="flex justify-between">
                  <span style={{ color: '#736c64' }}>Subtotal</span>
                  <span style={{ color: '#4a2c1a' }}>${subtotal.toFixed(2)}</span>
                </div>
                {discountAmount > 0 && (
                  <div className="flex justify-between" style={{ color: '#22c55e' }}>
                    <span>Discount ({appliedCode})</span>
                    <span>-${discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span style={{ color: '#736c64' }}>Shipping</span>
                  {freeShipping ? (
                    <span style={{ color: '#22c55e' }}>FREE</span>
                  ) : (
                    <span style={{ color: '#4a2c1a' }}>${shipping.toFixed(2)}</span>
                  )}
                </div>
                <div className="flex justify-between">
                  <span style={{ color: '#736c64' }}>Tax</span>
                  <span style={{ color: '#4a2c1a' }}>${tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-xl font-bold pt-2 border-t" style={{ borderColor: '#e5d5c0' }}>
                  <span style={{ color: '#4a2c1a' }}>Total</span>
                  <span style={{ color: '#FF8C19' }}>${total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
